import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted';
import { NumberFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/NumberFormatted';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useSecurityTypeTranslation } from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController';
import { PostInvestorJoinerStatistics } from '@dealstack/shared/lib/generated';
import styled from 'styled-components';
import { Tooltip as ReactTooltip } from 'react-tooltip';

interface Props {
	className?: string;
	investorsSummary?: PostInvestorJoinerStatistics;
}

const NewJoinersAndAllocationsInvestorsSummary = ({ investorsSummary, className }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const { mipData } = useMIPManageContext();

	const getSecurityTypeTranslation = useSecurityTypeTranslation();

	return (
		<div className={className}>
			<SummaryColumn>
				<SummaryRow>
					<Text variant="p-md" noWrap>
						{tMipCreate('investors.summary.number_of_individuals')}
					</Text>
					<Text variant="p-md" noWrap bold>
						{investorsSummary?.investorsCount}
					</Text>
				</SummaryRow>
			</SummaryColumn>
			<SummaryColumn>
				{investorsSummary?.bySecurity?.map((security) => (
					<SummaryRow key={`${security.securityId}${security.name}`}>
						<Text variant="p-md" noWrap>
							{getSecurityTypeTranslation(security.type)}
						</Text>
						{/* The normal TooltipText JSX won't work here, because nested sub-components cannot respect the width set by grid-template-columns. */}
						<Text variant="p-md" noWrap elipsis data-tooltip-id={security.securityId}>
							{security.name}
						</Text>
						<Text variant="p-md" noWrap bold center>
							<NumberFormatted value={security.totalNumber} />
						</Text>
						<Text variant="p-md" noWrap bold>
							<AmountFormatted
								value={security.totalCost}
								currency={mipData?.currency}
							/>
						</Text>
						<ReactTooltip id={security.securityId} content={security.name} />
					</SummaryRow>
				))}
			</SummaryColumn>
		</div>
	);
};

const SummaryColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.25em;
	width: clamp(10rem, 50%, 35rem);
`;

const SummaryRow = styled.div`
	display: grid;
	grid-template-columns: 7rem 1.5fr 1fr 1fr;
	grid-gap: 1em;

	${Text}:last-of-type {
		justify-self: end;
	}
`;

const Styled = styled(NewJoinersAndAllocationsInvestorsSummary)`
	overflow: auto;
	display: flex;
	justify-content: space-between;
	gap: 3em;
	padding: 1em 1.5em;
	border-right: 0.5rem;
	background-color: ${({ theme }) => theme.v2.color.neutral[100]};
`;

export { Styled as NewJoinersAndAllocationsInvestorsSummary };
